import { Helmet } from "react-helmet-async";
import { lcpPreloadProps, largestJpgUrl } from "./Picture";

const SITE_NAME = "Advanced Connected Technologies";
const DEFAULT_IMAGE = largestJpgUrl("hero_luxury_smart_home");

/**
 * Per-route SEO. `title` is the page name; we suffix " — Advanced Connected
 * Technologies" automatically unless `bare` is true.
 *
 * `lcpSlug` (optional) — slug of the page's above-the-fold LCP image.
 * When provided, we emit a <link rel="preload" as="image" type="image/avif">
 * for the AVIF srcset so the browser begins downloading the LCP image
 * before the React tree mounts.
 *
 * `image` (optional) — explicit override for the OG image URL. If not
 * provided and `lcpSlug` is set, we derive it from the manifest.
 */
export default function SEO({
    title,
    description,
    image,
    type = "website",
    bare = false,
    canonical,
    lcpSlug,
    lcpSizes = "100vw",
}) {
    const fullTitle = bare ? title : `${title} — ${SITE_NAME}`;
    const ogImage =
        image ||
        (lcpSlug ? largestJpgUrl(lcpSlug) : null) ||
        DEFAULT_IMAGE;
    const url =
        canonical ||
        (typeof window !== "undefined"
            ? window.location.origin + window.location.pathname
            : undefined);

    const lcp = lcpSlug ? lcpPreloadProps(lcpSlug, lcpSizes) : null;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />

            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:type" content={type} />
            {url && <meta property="og:url" content={url} />}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />

            {url && <link rel="canonical" href={url} />}

            {lcp && (
                <link
                    rel="preload"
                    as="image"
                    type="image/avif"
                    imageSrcSet={lcp.avifSrcSet}
                    imageSizes={lcp.sizes}
                    fetchPriority="high"
                />
            )}
        </Helmet>
    );
}
